import type { Variant } from 'types';
import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';

/**
 * Splits the implementation list into the three states that matter to whoever has to
 * write the code, and drops everything that is not one of them.
 *
 * The flat list conflated experiments that need nothing with experiments that are
 * waiting on work, which is the whole question this page exists to answer.
 *
 *  - deployed:      a variant is live now. Nothing to do; here for reference.
 *  - needsWork:     a variant won and nothing is live. This is the queue.
 *  - implemented:   explicitly marked done, whatever its deployment state.
 *  - excluded:      finished without a win. Not shown at all, see below.
 *
 * Order matters: "marked implemented" wins over everything, because it is an explicit
 * human statement and should not be overridden by inferred state. Deployment is checked
 * next, since a live variant is a fact rather than an inference. Only then does the
 * statistical verdict decide, and it decides on the sign of the result: an experiment
 * whose variant lost is not work, it is a decision already made. Membership of the
 * queue used to be "finished and not ticked", which put losers, flat tests and
 * under-powered tests in a list headed "needs implementation".
 */
export type ImplementationSection = 'deployed' | 'needsWork' | 'implemented';

/** Sections plus the one non-section: classified, then not listed. */
export type ImplementationClassification = ImplementationSection | 'excluded';

export type SectionedExperiments = {
  deployed: ExperimentWithVerdict[];
  needsWork: ExperimentWithVerdict[];
  implemented: ExperimentWithVerdict[];
  /**
   * Kept rather than discarded so the page can say how many rows it is not showing.
   * A page that silently omits half the data reads identically to a broken one.
   */
  excluded: ExperimentWithVerdict[];
};

const hasLiveDeployment = (experiment: ExperimentWithVerdict): boolean =>
  (experiment.variants ?? []).some((v: Variant) => !!v.deployed);

/**
 * Whether this experiment currently serves a deployed variant.
 *
 * Deployment is recorded on the variant and never cleared, so a variant that was rolled
 * out and then rolled back still carries the timestamp. Status is what says whether it
 * is live now, which is why both are consulted rather than just the variant.
 */
const isCurrentlyDeployed = (experiment: ExperimentWithVerdict): boolean =>
  hasLiveDeployment(experiment) && experiment.status === 'Deployed';

/** A win on either goal. The server decides what counts; this only reads the answer. */
export const hasWinner = (experiment: ExperimentWithVerdict): boolean => !!experiment.verdict?.winner;

export const classifyExperiment = (experiment: ExperimentWithVerdict): ImplementationClassification => {
  if (experiment.implementedAt) return 'implemented';
  if (isCurrentlyDeployed(experiment)) return 'deployed';
  if (hasWinner(experiment)) return 'needsWork';
  return 'excluded';
};

export const sectionExperiments = (experiments: readonly ExperimentWithVerdict[]): SectionedExperiments => {
  const sections: SectionedExperiments = { deployed: [], needsWork: [], implemented: [], excluded: [] };

  for (const experiment of experiments) {
    sections[classifyExperiment(experiment)].push(experiment);
  }

  return sections;
};

export const SECTION_META: Record<ImplementationSection, { title: string; description: string; emptyMessage: string }> =
  {
    deployed: {
      title: 'Deployed',
      description:
        'A variant is live on the site right now. Nothing to build: listed so the code and screenshots stay findable.',
      emptyMessage: 'No experiments are currently serving a deployed variant.',
    },
    needsWork: {
      title: 'Needs implementation',
      description:
        'A variant beat the control on conversions or on revenue, nothing is live and nobody has marked it done. This is the queue: experiments that lost, came out flat, or never gathered enough traffic are not listed, because there is nothing there to build.',
      emptyMessage: 'Nothing is waiting to be implemented.',
    },
    implemented: {
      title: 'Marked as implemented',
      description: 'Explicitly marked done. Kept separate so the queue above stays a queue.',
      emptyMessage: 'Nothing has been marked as implemented yet.',
    },
  };

/** Section order on the page: what needs doing is not buried under what does not. */
export const SECTION_ORDER: ImplementationSection[] = ['needsWork', 'deployed', 'implemented'];

const normalise = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Filter by the page's search box.
 *
 * This is filtered here rather than by each section's useDataControl, which was the
 * original approach and did nothing at all: that hook only filters when `searchKeys` is
 * set, the page never set it, so `hasSearch` was permanently false and typing in the box
 * had no effect on any of the three tables. Filtering the lists before they reach the
 * tables also removes the prop-to-state effect that pushed the query into three separate
 * controllers.
 *
 * Name and id are the searchable fields: id because the URL and support conversations
 * use it, and matching is diacritic-insensitive to match the behaviour elsewhere.
 */
export const filterExperiments = (
  experiments: readonly ExperimentWithVerdict[],
  query: string,
): ExperimentWithVerdict[] => {
  const needle = normalise(query.trim());
  if (needle.length === 0) return [...experiments];

  return experiments.filter(
    (experiment) => normalise(experiment.name ?? '').includes(needle) || String(experiment.id).includes(needle),
  );
};
